import { Drawer, DrawerContent, DrawerTitle } from '../ui/drawer';
import { PanelTransactionActions } from '@/client/components/trade/panel-transaction-actions';
import { Dialog, DialogContent, DialogTitle } from '@/client/components/ui/dialog';
import { useTradeOverlaySlot } from '@/client/store/trade-overlay-store';
import { cn } from '@/client/utils';
import { isInsideTransactionStatus } from '@/client/utils/transaction-status-target';
import { PropsWithChildren } from 'react';
import useMedia from 'react-use/lib/useMedia';

type DrawerDiaLogTitleProps = PropsWithChildren & {
  className?: string;
};
export const DrawerDiaLogTitle = ({ children, className }: DrawerDiaLogTitleProps) => {
  const isDesktop = useMedia('(min-width: 768px)');
  if (isDesktop) return <DialogTitle className={className}>{children}</DialogTitle>;
  return <DrawerTitle className={className}>{children}</DrawerTitle>;
};

export type DrawerDiaLogProps = PropsWithChildren & {
  className?: string;
  open?: boolean;
  setOpen?: (open: boolean) => void;
  disableOutside?: boolean;
  /**
   * The market whose trade UI this dialog hosts. On mobile, while open, the global
   * transaction toast hides for that market's records and the dialog shows their
   * recovery actions at the bottom instead. Records of other markets keep the toast.
   */
  transactionMarket?: string;
};
export const DrawerDiaLog = ({
  open,
  setOpen,
  children,
  className,
  disableOutside,
  transactionMarket,
}: DrawerDiaLogProps) => {
  const isDesktop = useMedia('(min-width: 768px)');
  const overlayOwner = useTradeOverlaySlot(!!open, transactionMarket);
  // The transaction toast floats above the dialog (outside its content). Tapping
  // it must not count as an outside interaction, or the dialog closes.
  const onInteractOutside = (e: { target: EventTarget | null; preventDefault: () => void }) => {
    if (disableOutside || isInsideTransactionStatus(e.target)) e.preventDefault();
  };
  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          aria-describedby={undefined}
          onInteractOutside={onInteractOutside}
          onOpenAutoFocus={e => e.preventDefault()}
          className={cn('gap-0 p-0 max-w-auto border-foreground/10 block', className)}
        >
          {children}
        </DialogContent>
      </Dialog>
    );
  }
  return (
    <Drawer open={open} onOpenChange={setOpen} repositionInputs={false}>
      <DrawerContent
        aria-describedby={undefined}
        onInteractOutside={onInteractOutside}
        onOpenAutoFocus={e => e.preventDefault()}
        className="w-full max-h-[calc(100%-52px)]"
      >
        <div className={cn('px-5 pb-5 h-full overflow-y-auto', className)}>
          {children}
          {/* Below the panel content, so it never overlaps the CTA. Mobile only (I2). */}
          {transactionMarket && <PanelTransactionActions owner={overlayOwner} />}
        </div>
      </DrawerContent>
    </Drawer>
  );
};
